import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight, MapPin, Search, Users } from 'lucide-react';
import { DatePickerField } from './DatePickerField';
import { useLang } from '../i18n';
import { useMediaOverrides } from '../utils/media';
import { useSiteContent } from '../utils/siteContent';
import { useCuratedCopy } from '../data/curatedArchitecture';

const SLIDES = [
  {
    img: '/media/4923818b-7917-45ff-9853-4bb847436818.jpeg',
    name: 'São Tomé.',
    lineKey: 'hero.s1.l2',
    ctaKey: 'hero.s1.cta',
    target: '/destinations/sao-tome-principe/sao-tome',
  },
  {
    img: 'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?q=80&w=1600&auto=format&fit=crop',
    name: 'Príncipe.',
    lineKey: 'hero.s2.l2',
    ctaKey: 'hero.s2.cta',
    target: '/destinations/sao-tome-principe/principe',
  },
  {
    img: '/media/56467cf4-1c33-4530-bae3-1167dc9c64fa.jpeg',
    name: 'Cabo Verde.',
    lineKey: 'hero.s3.l2',
    ctaKey: 'hero.s3.cta',
    target: '/destinations/cabo-verde',
  },
];

const Line = ({ text, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: '110%' }}
      animate={{ y: 0 }}
      exit={{ y: '-110%' }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

const AUTOPLAY_MS = 6000;
const RESUME_AFTER_MS = 12000;
const REDUCED_MOTION = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const Hero = () => {
  const [index, setIndex] = useState(0);
  const [where, setWhere] = useState('');
  const [date, setDate] = useState('');
  const [travellers, setTravellers] = useState('hero.trav2');
  const { t, lang } = useLang();
  const curated = useCuratedCopy(lang);
  const cms = useSiteContent('homepage');
  const ov = useMediaOverrides();
  const navigate = useNavigate();
  const ref = useRef(null);
  const pauseUntil = useRef(0);
  const touchStartX = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    if (REDUCED_MOTION) return undefined; // accessibility: no autoplay under reduced-motion
    const timer = setInterval(() => {
      if (Date.now() < pauseUntil.current) return; // paused after user interaction
      setIndex((i) => (i + 1) % SLIDES.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, []);

  const goTo = (next) => {
    pauseUntil.current = Date.now() + RESUME_AFTER_MS; // user interaction pauses autoplay
    setIndex(((next % SLIDES.length) + SLIDES.length) % SLIDES.length);
  };

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) > 48) goTo(index + (delta < 0 ? 1 : -1));
  };

  const slide = SLIDES[index];
  const slideImg = ov[`hero:${index}`] || slide.img;

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center justify-center overflow-hidden bg-eco-deep"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onPointerEnter={() => { pauseUntil.current = Date.now() + 60000; }}
      onPointerLeave={() => { pauseUntil.current = 0; }}
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <AnimatePresence mode="sync">
          <motion.div
            key={slideImg}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: REDUCED_MOTION ? 0 : 1.2, ease: 'easeInOut' }}
            className="absolute inset-0"
          >
            <img
              src={slideImg}
              alt=""
              fetchPriority={index === 0 ? 'high' : 'auto'}
              className={`absolute inset-0 h-full w-full object-cover ${REDUCED_MOTION ? '' : 'eco-kenburns'}`}
            />
          </motion.div>
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-eco-deep/70 via-eco-deep/35 to-eco-deep/75" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-4xl px-5 pt-24 pb-16 text-center">
        <motion.p
          initial={{ opacity: 0, letterSpacing: '0.6em' }}
          animate={{ opacity: 1, letterSpacing: '0.45em' }}
          transition={{ duration: 1 }}
          className="mb-6 text-[12px] font-semibold uppercase text-white/90 [text-shadow:0_1px_14px_rgba(18,46,38,0.65)]"
        >
          {cms.eyebrow || t('hero.kicker')}
        </motion.p>
        <AnimatePresence mode="wait">
          <motion.div key={index}>
            <h1 className="eco-hero-title">
              <Line text={cms.hero_title || slide.name} delay={0.1} />
              <Line text={cms.hero_subtitle || t(slide.lineKey)} delay={0.25} />
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base font-medium text-white/85 [text-shadow:0_1px_14px_rgba(18,46,38,0.65)] md:text-lg">{curated.hero}</p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-8"
            >
              <button
                data-testid="hero-cta-button"
                onClick={() => navigate(cms.primary_cta_target || slide.target)}
                className="group inline-flex min-h-12 items-center gap-2 rounded-[10px] bg-eco-gold px-6 text-[15px] font-semibold text-eco-deep transition-colors duration-200 hover:bg-[#b8944f]"
              >
                {cms.primary_cta_label || t(slide.ctaKey)}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        <motion.form
          id="pesquisa"
          data-testid="hero-search-bar"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8 }}
          onSubmit={(e) => {
            e.preventDefault();
            const query = new URLSearchParams();
            if (where) query.set('island', where);
            if (date) query.set('date', date);
            if (travellers) query.set('travellers', t(travellers));
            navigate(`/experiences${query.toString() ? `?${query}` : ''}`);
          }}
          className="mx-auto mt-12 grid w-full max-w-4xl gap-0 overflow-hidden rounded-2xl border border-white/20 bg-white p-2 text-left shadow-[0_20px_60px_rgba(0,0,0,0.22)] md:grid-cols-[1.25fr_1fr_1fr_auto]"
        >
          <label className="border-b border-eco-ink/10 px-5 py-4 md:border-r md:border-b-0">
            <span className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-eco-ink/45"><MapPin size={13} className="text-eco-gold" /> {t('search.destination')}</span>
            <select data-testid="hero-where-select" value={where} onChange={(e) => setWhere(e.target.value)} className="mt-1 w-full bg-transparent text-base font-medium text-eco-ink outline-none">
              <option value="">{t('search.destinationPlaceholder')}</option>
              <option value="São Tomé">São Tomé</option>
              <option value="Príncipe">Príncipe</option>
              <option value="Santiago">Santiago · Cabo Verde</option>
            </select>
          </label>
          <div className="border-b border-eco-ink/10 px-5 py-4 md:border-r md:border-b-0">
            <span className="block text-[12px] font-semibold uppercase tracking-[0.14em] text-eco-ink/45">{t('search.dates')}</span>
            <DatePickerField value={date} onChange={setDate} testid="hero-date-input" placeholder={t('search.datePlaceholder')} className="mt-1 min-h-0 border-transparent bg-transparent px-0 py-0 text-base font-medium shadow-none focus:ring-0" />
          </div>
          <label className="border-b border-eco-ink/10 px-5 py-4 md:border-r md:border-b-0">
            <span className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-eco-ink/45"><Users size={13} className="text-eco-gold" /> {t('search.travellers')}</span>
            <select data-testid="hero-who-select" value={travellers} onChange={(e) => setTravellers(e.target.value)} className="mt-1 w-full bg-transparent text-base font-medium text-eco-ink outline-none">
              <option value="hero.trav1">{t('hero.trav1')}</option>
              <option value="hero.trav2">{t('hero.trav2')}</option>
              <option value="hero.trav34">{t('hero.trav34')}</option>
              <option value="hero.trav5">{t('hero.trav5')}</option>
            </select>
          </label>
          <div className="flex p-2 md:pl-3">
            <button
              type="submit"
              data-testid="hero-explore-button"
              className="eco-btn-primary w-full px-7 md:w-auto"
            >
              <Search size={16} /> {t('search.cta')}
            </button>
          </div>
        </motion.form>
      </motion.div>

      <button
        data-testid="hero-prev-button"
        aria-label={t('a.prev')}
        onClick={() => goTo(index - 1)}
        className="absolute left-4 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/25 p-2.5 text-white/70 transition-colors hover:border-eco-gold hover:text-eco-gold md:left-8"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        data-testid="hero-next-button"
        aria-label={t('a.next')}
        onClick={() => goTo(index + 1)}
        className="absolute right-4 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/25 p-2.5 text-white/70 transition-colors hover:border-eco-gold hover:text-eco-gold md:right-8"
      >
        <ChevronRight size={20} />
      </button>

      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-dot-${i}`}
            aria-label={`Slide ${i + 1}`}
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? 'w-7 bg-eco-gold' : 'w-1.5 bg-white/40 hover:bg-white/70'}`}
          />
        ))}
      </div>
    </section>
  );
};
